import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import Icon from '../../components/AppIcon';
import { useLanguage } from '../../contexts/LanguageContext';

const ForgotPassword = () => {
  const { t } = useLanguage();
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setError('');
    setIsLoading(true);
    try {
      // Use the published app URL so the reset link works on the live domain
      // Falls back to current origin for local/preview environments
      const appOrigin = window.location?.hostname === 'localhost' || window.location?.hostname?.includes('builtwithrocket')
        ? window.location?.origin
        : 'https://app.servicejockey.net';

      const { error: resetError } = await supabase?.auth?.resetPasswordForEmail(email, {
        redirectTo: `${appOrigin}/reset-password`,
      });
      if (resetError) {
        setError(resetError?.message || 'Failed to send reset email. Please try again.');
      } else {
        setSent(true);
      }
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{ background: 'linear-gradient(135deg, #111111 0%, #1a0000 50%, #111111 100%)' }}>
      {/* Yellow accent stripe at top */}
      <div className="fixed top-0 left-0 right-0 h-1" style={{ background: '#FFD700' }}></div>

      <div className="w-full max-w-md">
        {/* Logo / Brand */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl mb-4 shadow-2xl" style={{ background: '#CC0000' }}>
            <Icon name="Wrench" size={38} color="white" />
          </div>
          <h1 className="text-4xl font-bold tracking-widest uppercase" style={{ color: '#FFFFFF', letterSpacing: '0.15em' }}>
            SERVICE<span style={{ color: '#FFD700' }}>JOCKEY</span>
          </h1>
          <div className="flex items-center justify-center gap-2 mt-2">
            <div className="h-px w-12" style={{ background: '#CC0000' }}></div>
            <p className="text-xs font-semibold uppercase tracking-widest" style={{ color: '#FFD700' }}>Field Service Management</p>
            <div className="h-px w-12" style={{ background: '#CC0000' }}></div>
          </div>
        </div>

        {/* Card */}
        <div className="rounded-2xl shadow-2xl p-8" style={{ background: '#1A1A1A', border: '1px solid #333333' }}>
          {!sent ? (
            <>
              <div className="mb-6">
                <h2 className="text-xl font-semibold mb-1" style={{ color: '#FFFFFF' }}>{t('fp_title')}</h2>
                <p className="text-sm" style={{ color: '#999999' }}>{t('fp_subtitle')}</p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                {error && (
                  <div className="rounded-lg p-3 flex items-center gap-2" style={{ background: 'rgba(204,0,0,0.15)', border: '1px solid #CC0000' }}>
                    <Icon name="AlertCircle" size={16} color="#FF4444" />
                    <p className="text-sm" style={{ color: '#FF4444' }}>{error}</p>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium mb-1.5" style={{ color: '#CCCCCC' }}>{t('fp_emailAddress')}</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e?.target?.value)}
                    placeholder={t('fp_emailPlaceholder')}
                    required
                    className="w-full px-4 py-2.5 rounded-lg text-sm transition-colors"
                    style={{
                      background: '#111111',
                      border: '1px solid #444444',
                      color: '#FFFFFF',
                      outline: 'none',
                    }}
                    onFocus={(e) => { e.target.style.borderColor = '#CC0000'; e.target.style.boxShadow = '0 0 0 2px rgba(204,0,0,0.25)'; }}
                    onBlur={(e) => { e.target.style.borderColor = '#444444'; e.target.style.boxShadow = 'none'; }}
                  />
                </div>

                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full flex items-center justify-center gap-2 font-bold py-3 px-4 rounded-xl transition-all duration-200 mt-2 uppercase tracking-wider text-sm"
                  style={{
                    background: isLoading ? '#991111' : '#CC0000',
                    color: '#FFFFFF',
                    boxShadow: '0 4px 15px rgba(204,0,0,0.4)',
                    cursor: isLoading ? 'not-allowed' : 'pointer',
                  }}
                  onMouseEnter={(e) => { if (!isLoading) { e.target.style.background = '#FF0000'; e.target.style.boxShadow = '0 6px 20px rgba(204,0,0,0.6)'; }}}
                  onMouseLeave={(e) => { if (!isLoading) { e.target.style.background = '#CC0000'; e.target.style.boxShadow = '0 4px 15px rgba(204,0,0,0.4)'; }}}
                >
                  {isLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      <span>{t('fp_sending')}</span>
                    </>
                  ) : (
                    <>
                      <Icon name="Mail" size={18} color="white" />
                      <span>{t('fp_sendResetLink')}</span>
                    </>
                  )}
                </button>
              </form>
            </>
          ) : (
            <div className="text-center py-4">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-4" style={{ background: 'rgba(204,0,0,0.15)', border: '1px solid #CC0000' }}>
                <Icon name="MailCheck" size={32} color="#CC0000" />
              </div>
              <h2 className="text-xl font-semibold mb-2" style={{ color: '#FFFFFF' }}>{t('fp_checkInbox')}</h2>
              <p className="text-sm mb-1" style={{ color: '#999999' }}>
                {t('fp_sentTo')}
              </p>
              <p className="text-sm font-semibold mb-4" style={{ color: '#FFD700' }}>{email}</p>
              <p className="text-xs" style={{ color: '#666666' }}>
                {t('fp_didntReceive')}{' '}
                <button
                  onClick={() => { setSent(false); setError(''); }}
                  className="underline"
                  style={{ color: '#CC0000', background: 'none', border: 'none', cursor: 'pointer' }}
                >
                  {t('fp_tryAgain')}
                </button>.
              </p>
            </div>
          )}

          <div className="mt-6 pt-5" style={{ borderTop: '1px solid #2A2A2A' }}>
            <Link
              to="/login"
              className="flex items-center justify-center gap-2 text-sm transition-colors"
              style={{ color: '#999999' }}
              onMouseEnter={(e) => e.currentTarget.style.color = '#FFFFFF'}
              onMouseLeave={(e) => e.currentTarget.style.color = '#999999'}
            >
              <Icon name="ArrowLeft" size={16} />
              {t('fp_backToSignIn')}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
